import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { mediaUrl, formatApiError } from '@/lib/api';
import { ecommerceService as svc } from '@/services/ecommerce.service';
import { useCart } from '@/context/CartContext';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo, productJsonLd } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AddProductButton } from '@/components/cart/AddProductButton';
import { toast } from 'sonner';
import { ImageIcon, ChevronRight, ShoppingCart, Check, PackageX, ArrowRight, Truck, Star, ThumbsUp, BadgeCheck, MessageSquarePlus, Plus, Minus, CreditCard, ShieldCheck, PackageCheck, Clock, MapPin, Loader2 } from 'lucide-react';

const eur = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const dshort = (s) => (s ? new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' }) : '');

function Stars({ value = 0 }) {
  return (
    <span className="inline-flex" aria-label={`${value} su 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />)}
    </span>
  );
}

function ProductReviews({ ownerId, productId, productName }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ order_number: '', email: '', author: '', rating: 5, title: '', body: '' });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try { setData(await svc.pubReviews(ownerId, productId)); } catch (e) { setData({ count: 0, avg_rating: 0, distribution: {}, items: [] }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ownerId, productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) { toast.error('Scrivi il testo della recensione'); return; }
    setBusy(true);
    try {
      const res = await svc.pubReviewSubmit({ owner_id: ownerId, product_id: productId, ...form, rating: Number(form.rating) });
      toast.success(res.message || 'Recensione inviata');
      setForm({ order_number: '', email: '', author: '', rating: 5, title: '', body: '' });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invio non riuscito. Verifica numero ordine ed email.');
    } finally { setBusy(false); }
  };

  const helpful = async (id) => {
    try { await svc.pubReviewHelpful(ownerId, id); load(); } catch (e) { /* noop */ }
  };

  return (
    <div className="mt-12 border-t border-border pt-8" data-testid="product-reviews-public">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-foreground">Recensioni</h2>
          {data && data.count > 0 ? (
            <div className="mt-1 flex items-center gap-2"><Stars value={Math.round(data.avg_rating)} /><span className="text-sm text-muted-foreground">{data.avg_rating.toFixed(1)} su 5 · {data.count} recensioni</span></div>
          ) : <p className="mt-1 text-sm text-muted-foreground">Ancora nessuna recensione. Sii il primo!</p>}
        </div>
        <Button variant="outline" onClick={() => setOpen((v) => !v)} data-testid="review-write-btn"><MessageSquarePlus className="mr-2 h-4 w-4" />Scrivi una recensione</Button>
      </div>

      {open && (
        <form onSubmit={submit} className="mt-4 rounded-2xl border border-border bg-card p-5" data-testid="review-form">
          <p className="mb-3 text-sm text-muted-foreground">Per pubblicare una recensione conferma l’acquisto con <strong>numero ordine</strong> ed <strong>email</strong>. Sarà pubblicata dopo la verifica.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Numero ordine</Label><Input value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} placeholder="Es. SE-1001" data-testid="review-order" /></div>
            <div><Label>Email dell’ordine</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="la-tua@email.it" data-testid="review-email" /></div>
            <div><Label>Nome (facoltativo)</Label><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Come firmarti" data-testid="review-author" /></div>
            <div>
              <Label>Voto</Label>
              <div className="mt-2 flex gap-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} aria-label={`${i} stelle`}>
                    <Star className={`h-6 w-6 transition-colors ${i <= form.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40 hover:text-amber-300'}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3"><Label>Titolo (facoltativo)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title" /></div>
          <div className="mt-3"><Label>La tua recensione</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder={`Cosa pensi di ${productName || 'questo prodotto'}?`} data-testid="review-body" /></div>
          <div className="mt-4 flex gap-2"><Button type="submit" disabled={busy} data-testid="review-submit">{busy ? 'Invio…' : 'Invia recensione'}</Button><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annulla</Button></div>
        </form>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-5 space-y-4" data-testid="review-list">
          {data.items.map((r) => (
            <div key={r.id} className="rounded-xl border border-border p-4" data-testid={`pub-review-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{r.author}</span>
                <Stars value={r.rating} />
                {r.verified_purchase && <Badge variant="secondary" className="gap-1 font-normal"><BadgeCheck className="h-3 w-3" />Acquisto verificato</Badge>}
                <span className="text-xs text-muted-foreground">· {dshort(r.created_at)}</span>
              </div>
              {r.title && <p className="mt-2 font-semibold text-foreground">{r.title}</p>}
              <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
              {r.reply && <div className="mt-2 rounded-lg border-l-2 border-primary bg-muted/40 p-2 text-sm"><span className="font-medium text-primary">Risposta del venditore:</span> {r.reply}</div>}
              <button type="button" onClick={() => helpful(r.id)} className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary" data-testid={`helpful-${r.id}`}><ThumbsUp className="h-3.5 w-3.5" />Utile ({r.helpful_count})</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function RelatedCard({ ownerId, p }) {
  return (
    <Link to={`/negozio/${ownerId}/${p.slug}`} className="group block overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-transform duration-300 hover:-translate-y-1" data-testid={`related-${p.id}`}>
      <div className="relative aspect-square overflow-hidden bg-muted">
        {p.main_image ? <img src={mediaUrl(p.main_image)} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-7 w-7 text-muted-foreground" /></div>}
      </div>
      <div className="p-3">
        <h4 className="line-clamp-1 text-sm font-medium text-foreground">{p.name}</h4>
        <span className="mt-1 block font-display text-sm font-bold text-foreground">{eur(p.effective_price)}</span>
      </div>
    </Link>
  );
}

export default function ShopProduct() {
  const { ownerId, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addProduct, isCustomer } = useCart() || {};
  const [p, setP] = useState(null);
  const [error, setError] = useState('');
  const [img, setImg] = useState('');
  const [variantId, setVariantId] = useState('');
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    let alive = true;
    setP(null); setError(''); setImg(''); setVariantId(''); setQty(1);
    (async () => {
      try {
        const d = await svc.publicShopProduct(ownerId, slug);
        if (alive) {
          setP(d);
          setImg((d.gallery && d.gallery[0]) || d.main_image || '');
          if (d.has_variants && (d.variants || []).length) setVariantId(d.variants[0].id);
        }
      } catch (e) { if (alive) setError('Prodotto non trovato o non disponibile.'); }
    })();
    return () => { alive = false; };
  }, [ownerId, slug]);

  const selVariant = p && p.has_variants ? (p.variants || []).find((v) => v.id === variantId) : null;
  const shownPrice = selVariant ? selVariant.price : (p ? p.effective_price : 0);
  const maxStock = p ? (selVariant ? selVariant.stock : p.stock) : 0;
  const canBuy = p ? (p.product_type === 'digital' || maxStock > 0) && (!p.has_variants || !!variantId) : false;
  const ship = p?.shipping || null;
  const isPhysical = p ? (p.product_type !== 'digital' && p.product_type !== 'virtual') : false;

  const buyNow = async () => {
    if (!p || !canBuy) return;
    const payload = { owner_id: ownerId, product_id: p.id, variant_id: p.has_variants ? variantId : null, qty };
    if (!isCustomer) {
      try { sessionStorage.setItem('sila_pending_add', JSON.stringify({ type: 'product', ...payload })); } catch (e) { /* noop */ }
      toast.info('Accedi per completare l\'acquisto');
      navigate(`/cliente/accedi?next=${encodeURIComponent('/cliente/carrello')}`);
      return;
    }
    setBuying(true);
    try {
      await addProduct(payload);
      navigate('/cliente/carrello');
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || 'Impossibile procedere all\'acquisto');
    } finally { setBuying(false); }
  };

  return (
    <PublicLayout>
      <Seo
        title={p ? `${p.name} — Shop Sila` : 'Prodotto — Shop Sila'}
        description={p?.short_description || ''}
        image={p?.main_image ? mediaUrl(p.main_image) : undefined}
        type="product"
        jsonLd={p ? productJsonLd({
          name: p.name,
          description: p.short_description || '',
          image: p.main_image ? mediaUrl(p.main_image) : undefined,
          price: p.effective_price,
          currency: 'EUR',
          url: (typeof window !== 'undefined' ? window.location.href : undefined),
          availability: ((p.stock || 0) > 0 || (p.variants || []).some((v) => (v.stock || 0) > 0) || p.product_type === 'digital') ? 'InStock' : 'OutOfStock',
          brand: p.shop?.name || 'Sila Experiences',
        }) : undefined}
      />

      <section className="sila-section py-8" data-testid="product-page">
        {error ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="product-error">
            <PackageX className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-3 font-medium text-foreground">{error}</p>
            <Button asChild variant="outline" className="mt-4"><Link to="/shop">Torna allo Shop</Link></Button>
          </div>
        ) : !p ? (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Skeleton className="aspect-square w-full rounded-2xl" />
            <div className="space-y-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-6 w-1/3" /><Skeleton className="h-24 w-full" /><Skeleton className="h-11 w-40" /></div>
          </div>
        ) : (
          <>
            <nav className="mb-5 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              <Link to="/shop" className="hover:text-foreground">Shop</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/negozio/${ownerId}`} className="hover:text-foreground">{p.shop?.name || 'Negozio'}</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground">{p.name}</span>
            </nav>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* GALLERIA */}
              <div>
                <div className="relative aspect-square overflow-hidden rounded-2xl border border-border bg-muted">
                  {img ? <img src={mediaUrl(img)} alt={p.name} className="h-full w-full object-cover" data-testid="product-main-image" /> : <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-10 w-10 text-muted-foreground" /></div>}
                  {p.on_sale && <span className="absolute left-4 top-4 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">Offerta</span>}
                </div>
                {(p.gallery || []).length > 1 && (
                  <div className="mt-3 flex gap-2">
                    {p.gallery.map((g, i) => (
                      <button key={i} type="button" onClick={() => setImg(g)} className={`h-16 w-16 overflow-hidden rounded-lg border-2 transition-colors ${img === g ? 'border-primary' : 'border-border hover:border-primary/50'}`} data-testid={`thumb-${i}`}>
                        <img src={mediaUrl(g)} alt={`${p.name} ${i + 1}`} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* INFO */}
              <div>
                {p.category && <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{p.category}</span>}
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-foreground" data-testid="product-title">{p.name}</h1>
                {(p.badges || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">{p.badges.map((b) => <Badge key={b} variant="secondary" className="font-normal">{b}</Badge>)}</div>
                )}
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="font-display text-3xl font-bold text-foreground" data-testid="product-price">{eur(shownPrice)}</span>
                  {p.on_sale && !selVariant && <span className="text-lg text-muted-foreground line-through">{eur(p.price)}</span>}
                  <span className="text-sm text-muted-foreground">/ {p.unit}</span>
                </div>
                <div className="mt-3">
                  {canBuy ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary"><Check className="h-4 w-4" />Disponibile</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><PackageX className="h-4 w-4" />Esaurito</span>
                  )}
                </div>
                {p.short_description && <p className="mt-4 text-muted-foreground">{p.short_description}</p>}

                {p.has_variants && (p.variants || []).length > 0 && (
                  <div className="mt-5 max-w-xs">
                    <Label className="text-sm">Variante</Label>
                    <Select value={variantId} onValueChange={(v) => { setVariantId(v); setQty(1); }}>
                      <SelectTrigger data-testid="variant-select"><SelectValue placeholder="Scegli una variante" /></SelectTrigger>
                      <SelectContent>
                        {p.variants.map((v) => (
                          <SelectItem key={v.id} value={v.id} disabled={p.product_type !== 'digital' && v.stock <= 0}>
                            {v.name} · {eur(v.price)}{p.product_type !== 'digital' && v.stock <= 0 ? ' (esaurito)' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <div className="inline-flex items-center rounded-lg border border-border" data-testid="qty-stepper">
                    <Button type="button" variant="ghost" size="icon" className="h-11 w-11 rounded-r-none" disabled={qty <= 1} onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Diminuisci"><Minus className="h-4 w-4" /></Button>
                    <span className="w-10 text-center font-medium tabular-nums" data-testid="qty-value">{qty}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-11 w-11 rounded-l-none" disabled={p.product_type !== 'digital' && qty >= Math.max(1, maxStock)} onClick={() => setQty((q) => q + 1)} aria-label="Aumenta"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <AddProductButton ownerId={ownerId} productId={p.id} variantId={p.has_variants ? variantId : null} qty={qty}
                    title={p.name} size="lg" variant="outline" label="Aggiungi al carrello" disabled={!canBuy} />
                  <Button size="lg" onClick={buyNow} disabled={!canBuy || buying} data-testid="buy-now-btn">
                    {buying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}Compra ora
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-4">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="stripe-secure-note"><ShieldCheck className="h-3.5 w-3.5 text-primary" />Pagamento sicuro con Stripe</span>
                  <Link to={`/negozio/${ownerId}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">Continua lo shopping <ArrowRight className="h-3.5 w-3.5" /></Link>
                </div>

                {isPhysical && (
                  <div className="mt-5 rounded-xl border border-border bg-muted/30 p-4" data-testid="product-shipping-estimate">
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                      {ship && ship.from_price != null ? (
                        <span className="flex items-center gap-2 font-medium text-foreground"><Truck className="h-4 w-4 text-primary" />Spedizione stimata da <span className="font-display font-bold">{eur(ship.from_price)}</span></span>
                      ) : (
                        <span className="flex items-center gap-2 font-medium text-foreground"><Truck className="h-4 w-4 text-primary" />Spedizione in tutta Italia</span>
                      )}
                      {ship && ship.free_threshold != null && (
                        <span className="flex items-center gap-1.5 text-muted-foreground"><PackageCheck className="h-4 w-4 text-primary" />Gratuita sopra {eur(ship.free_threshold)}</span>
                      )}
                      {ship && ship.prep_days > 0 && (
                        <span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-4 w-4" />Pronta in {ship.prep_days} {ship.prep_days === 1 ? 'giorno' : 'giorni'}</span>
                      )}
                      {ship && ship.pickup && (
                        <span className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-4 w-4" />Ritiro in sede</span>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Costo definitivo calcolato al checkout in base all'indirizzo (corrieri Packlink).</p>
                  </div>
                )}

                {p.description && (
                  <div className="mt-6 border-t border-border pt-6">
                    <h2 className="font-display text-lg font-semibold text-foreground">Descrizione</h2>
                    <p className="mt-2 whitespace-pre-line text-muted-foreground">{p.description}</p>
                  </div>
                )}
              </div>
            </div>

            {(p.related || []).length > 0 && (
              <div className="mt-12">
                <h2 className="font-display text-xl font-bold tracking-tight text-foreground">Potrebbe interessarti</h2>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="related-grid">
                  {p.related.map((r) => <RelatedCard key={r.id} ownerId={ownerId} p={r} />)}
                </div>
              </div>
            )}

            <ProductReviews ownerId={ownerId} productId={p.id} productName={p.name} />
          </>
        )}
      </section>
    </PublicLayout>
  );
}
